import React, { useState, useEffect } from 'react';
import { userGroupService } from '../services';
import { DataTable } from '../components';
import { useSideDialog, useModalDialog, useGlobalState, useMessage } from '../hooks';
import { handleError, Role, authMethod } from '../helpers';

export const UserGroups = () => {
  const [userGroups, setUserGroups] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    const promise = new Promise((resolve, reject) => {
      userGroupService
        .getAll()
        .then(res => {
          setUserGroups([...res.user_groups]);
          setLoading(false);
          resolve(res.user_groups);
        })
        .catch(err => {
          setLoading(false);
          showMessage(err, 'error');
        });
    });

    Promise.all([promise, fetchMemberCount(), fetchOwnedItemsCount()])
      .then(result => {
        updateMemberCount(result[0], result[1]);
        updateOwnedItemsCount(result[0], result[2]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchOwnedItemsCount = async () => {
    return new Promise((resolve, reject) => {
      userGroupService
        .getOwnedItemsTotalCount()
        .then(res => {
          resolve(res.owned_items_count);
        })
        .catch(err => {
          return null;
        });
    });
  };

  const updateOwnedItemsCount = (data, statistics) => {
    let dataTmp = data;
    let statistic = {};
    let tmp = null;

    let maxValue = 0;

    statistics.forEach(row => {
      if (row.owned_items_count > maxValue) {
        maxValue = row.owned_items_count;
      }
    });

    setUserGroups(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.user_group_id === item.user_group_id);
        return {
          ...item,
          owned_items_count: statistic ? statistic.owned_items_count : 0,
          owned_items_count_max: maxValue,
        };
      })
    );
  };

  const fetchMemberCount = async () => {
    return new Promise((resolve, reject) => {
      userGroupService
        .getMemberCount()
        .then(res => {
          resolve(res.member_count);
        })
        .catch(err => {
          return null;
        });
    });
  };

  const updateMemberCount = (data, statistics) => {
    let dataTmp = data;
    let statistic = {};
    let tmp = null;

    let maxValue = 0;

    statistics.forEach(row => {
      if (row.member_count > maxValue) {
        maxValue = row.member_count;
      }
    });

    setUserGroups(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.user_group_id === item.user_group_id);
        return {
          ...item,
          member_count: statistic ? statistic.member_count : 0,
          member_count_max: maxValue,
        };
      })
    );
  };

  const addUserGroup = async data => {
    const user_groups = { user_groups: Array.isArray(data) ? data : [data] };

    await userGroupService
      .create(user_groups)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message, res.warning ? 'warning' : undefined);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editUserGroup = async data => {
    const user_groups = { user_groups: Array.isArray(data) ? data : [data] };

    await userGroupService
      .update(user_groups)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteUserGroup = async data => {
    await userGroupService
      .destroy({ user_groups: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const userGroupTypeValues = Object.entries(authMethod).reduce(
    (a, [key, value]) => ({ ...a, [key]: { label: value } }),
    {}
  );
  const userProvisioningValues = {
    true: { label: 'Enabled', className: 'pill pill-black' },
    false: { label: 'Disabled', className: 'pill pill-grey' },
  };

  return (
    <div className="Users wide-margin">
      <DataTable
        columns={[
          {
            id: 'user_group_name',
            name: '',
            type: 'owner',
            link: '/user_groups/:user_group_id',
            data: { photo: 'user_group_photo' },
          },
          {
            id: 'user_group_name',
            name: 'Name',
            link: '/user_groups/:user_group_id',
            className: 'bold',
          },
          {
            id: 'user_group_description',
            name: 'Description',
            type: 'description',
            viewMode: 'descriptions',
          },
          {
            id: 'member_count',
            name: 'Members',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'member_count', max: 'member_count_max' },
            viewMode: 'statistics',
          },
          {
            id: 'owned_items_count',
            name: 'Assigned Assets',
            type: 'chart',
            chartType: 'bar',
            data: { data: 'owned_items_count', max: 'owned_items_count_max' },
            viewMode: 'statistics',
          },
          {
            id: 'user_group_type',
            name: 'Type',
            className: 'small-text',
            value: 'user_group_type',
            values: userGroupTypeValues,
          },
          { id: 'user_group_default_role_name', name: 'Default Role' },
          {
            id: 'user_provisioning_is_enabled',
            name: 'User Provisioning',
            type: 'boolean',
            values: userProvisioningValues,
          },
        ]}
        buttons={[
          {
            label: '+ Add',
            action: 'add',
            mainButton: true,
            onClick: items => showSideDialog('addUserGroup', items || selectedItems, addUserGroup),
          },
          {
            label: 'Edit',
            action: 'edit',
            onClick: items =>
              showSideDialog('editUserGroup', items || selectedItems, editUserGroup),
          },
          {
            label: 'Delete',
            action: 'delete',
            onClick: items =>
              showModalDialog('deleteUserGroup', items || selectedItems, deleteUserGroup),
          },
        ]}
        data={userGroups}
        title="User Groups"
        idColumn="user_group_id"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        loading={loading}
        editable={editable}
        showViewModes={true}
        filterable={true}
        filterObjects={[
          'user_group_name',
          'user_group_description',
          'user_group_type',
          'user_group_default_role_name',
          'user_provisioning_is_enabled',
        ]}
        filterObjectsValueMap={{ user_group_type: userGroupTypeValues }}
      />
    </div>
  );
};
